import React, { useEffect, useState } from 'react'

import _ from 'lodash'
import { connect } from 'react-redux'
import pluralize from 'pluralize'
import { Link, withRouter, RouteComponentProps } from 'react-router-dom'
import { Container, Table, Card, Form, Button, Badge } from 'react-bootstrap'

import { userCan } from 'src/store/user/userActions'
import CompetitionService from 'src/services/CompetitionService'
import { paginate } from 'src/utils/filters'
import { formatCurrency } from 'src/utils/money'
import { route } from 'src/utils/routes'
import { asset } from 'src/utils/file'
import { IState } from 'src/store'
import { IProduct } from 'src/services/types/product'

import Error401 from 'src/pages/Error/401.page'
import Header from 'src/components/Layout/Header/Header'
import Pagination from 'src/components/Pagination/Pagination'
import NotFound from 'src/components/Error/NotFound'
import ProductImage from 'src/components/Image/ProductImage'

const competitionService = new CompetitionService()

interface IProps extends RouteComponentProps {
  user: any
  userCan: (permission: string) => boolean
}

const Competition: React.FC<IProps> = ({ user, userCan, history }) => {
  const _isMounted = React.useRef(false)

  // indicators
  const [isLoading, setIsLoading] = useState<boolean>(true)

  // All products
  const [products, setProducts] = useState<IProduct[]>()
  // Products based on searchTerm and/or status
  const [sortedProducts, setSortedProducts] = useState<IProduct[]>()

  const [searchTerm, setSearchTerm] = useState<string>()
  const [page, setPage] = useState(1)

  useEffect(() => {
    _isMounted.current = true

    if (userCan('view_competition')) {
      setIsLoading(true)
      competitionService
        .fetchByManufacturer(user.manufacturer_id)
        .then((products) => {
          if (_isMounted.current) {
            setProducts(products)
            setSortedProducts(products)
            setIsLoading(false)
          }
        })
    }

    return () => {
      _isMounted.current = false
    }
  }, [userCan, user.manufacturer_id])

  const handleSearch = _.debounce((query) => {
    setPage(1)
    if (!query) setSortedProducts(products)
    else {
      setSortedProducts(
        products.filter((product) => {
          return (
            product.name.search(new RegExp(query, 'i')) >= 0 ||
            product.status.search(new RegExp(query, 'i')) >= 0
          )
        })
      )
    }
  }, 150)

  /**
   * Get the products to be shown on the current page
   */
  const paginatedProducts = React.useMemo(() => {
    if (sortedProducts) {
      return paginate(sortedProducts, page)
    } else return []
  }, [page, sortedProducts])

  if (!userCan('view_competition')) return <Error401 />

  return (
    <>
      <Header title="Competition Products" />
      <div className="page-container">
        <Container>
          <Card>
            <Card.Header>
              <div className="w-100 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <div>
                    {sortedProducts
                      ? `${sortedProducts.length} ${pluralize(
                          'competitition product',
                          sortedProducts.length
                        )}`
                      : '0 competitition products'}
                  </div>
                  <div className="ml-3">
                    <Form.Control
                      size="sm"
                      type="text"
                      name="searchTerm"
                      value={searchTerm || ''}
                      className="search"
                      style={{ minWidth: '220px' }}
                      placeholder="Search by name or status"
                      onChange={({ target }) => {
                        setSearchTerm(target.value)
                        handleSearch(target.value)
                      }}
                      disabled={isLoading || !sortedProducts}
                    />
                  </div>
                </div>

                {userCan('create_competition') && (
                  <div>
                    <Link to={route('competition_create')}>
                      <Button size="sm">Create Competition</Button>
                    </Link>
                  </div>
                )}
              </div>
            </Card.Header>
            <Card.Body className="px-0 py-0">
              {!isLoading && paginatedProducts.length ? (
                <>
                  <div className="table-wrapper padded">
                    <Table className="table-fit" hover responsive>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th className="text-center">--</th>
                          <th>Name</th>
                          <th>Status</th>
                          <th className="text-right">Size (ML)</th>
                          <th className="text-right">Price (₦)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedProducts.map((product, index) => (
                          <tr
                            key={product._id}
                            className="cursor-pointer"
                            onClick={() =>
                              history.push(
                                route('competition_update', {
                                  id: product._id,
                                })
                              )
                            }
                          >
                            <td className="align-middle">
                              {index + 1 + (page - 1) * 10}
                            </td>
                            <td className="align-middle text-center">
                              <div className="d-flex justify-content-center">
                                <ProductImage
                                  src={
                                    product.upload
                                      ? asset(product.upload.path)
                                      : false
                                  }
                                  roundedCircle
                                />
                              </div>
                            </td>
                            <td className="align-middle">
                              <span
                                className="text-truncated"
                                title={product.name}
                              >
                                {product.name}
                              </span>
                            </td>

                            <td className="align-middle">
                              <Badge
                                className="text-capitalize"
                                variant={
                                  product.status === 'active'
                                    ? 'success'
                                    : 'danger'
                                }
                              >
                                {product.status}
                              </Badge>
                            </td>
                            <td className="align-middle text-currency text-right">
                              {product.size}
                            </td>
                            <td className="align-middle text-currency font-weight-bold text-right">
                              {formatCurrency(product.price, true)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  <Pagination
                    className="my-4"
                    perPage={10}
                    totalItems={sortedProducts.length}
                    currentPage={page}
                    onChange={(page) => setPage(page)}
                  />
                </>
              ) : null}

              {!isLoading && !paginatedProducts.length ? (
                <div className="my-5">
                  <NotFound summary="No products found" />
                </div>
              ) : null}

              {isLoading && (
                <div className="d-flex justify-content-center py-5">
                  <figure className="spinner primary" />
                </div>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>
    </>
  )
}

export default connect((state: IState) => ({ user: state.user }), { userCan })(
  withRouter(Competition)
)
